/**
 * Phase 1 — Redirect-Aware Login (Feb 2026).
 *
 * Utility for safely resolving a post-login redirect target. Used by
 * LoginPage, RegisterPage, and ProtectedRoute so email deep-links
 * (e.g. Promo Agent, Daily Brief) can send a user to `/customer/orders/ABC`
 * and — even if the user has to log in on the way — they land on that
 * exact page instead of the generic role dashboard.
 *
 * Security surface:
 *   1. Only same-origin RELATIVE paths ("/customer/…") are honoured.
 *      Any absolute URL, protocol-relative URL, or anything containing
 *      `://` is rejected. Prevents open-redirect attacks where a
 *      malicious `?next=https://evil.com` would exfiltrate a freshly
 *      logged-in user off-site.
 *   2. The path must match the user's role via ROLE_PATH_PREFIXES —
 *      a customer can only land on `/customer/*` or public routes;
 *      no privilege confusion via a crafted `?next=/dispatch/…`.
 *   3. Falls back to the role's default dashboard on any rejection —
 *      never leaves the user on `/login` with no navigation.
 */

// Same shape as ProtectedRoute + LoginPage's inline map. Kept here as the
// single source of truth for role → default dashboard.
export const ROLE_DASHBOARDS = {
  customer: '/customer',
  driver: '/driver',
  dispatcher: '/dispatch',
  super_admin: '/dispatch',
};

// Prefixes each role is permitted to be redirected TO. Any deeper path
// under these prefixes is allowed (e.g. `/customer/orders/ABC` for
// customer). Public routes are always allowed regardless of role.
const ROLE_PATH_PREFIXES = {
  customer: ['/customer'],
  driver: ['/driver'],
  dispatcher: ['/dispatch'],
  // super_admin sees dispatcher surfaces + can preview customer/driver
  // routes (already handled by ProtectedRoute), so allow those too.
  super_admin: ['/dispatch', '/customer', '/driver'],
};

const PUBLIC_PATH_PREFIXES = [
  '/privacy', '/terms', '/contact', '/preview', '/design-preview',
];

// Anything that would let a caller escape same-origin.
const REJECT_SUBSTRINGS = ['://', '\\', '\n', '\r', '\t', '\0'];

/**
 * Extract the raw `next` candidate from the current URL / router state.
 * Query-string `?next=` wins over React Router state.from, because email
 * links use ?next= (state doesn't survive a cold PWA launch).
 *
 * @param {object} location — React Router location (from useLocation()).
 * @returns {string|null} — decoded raw path, or null if none present.
 */
export function extractNextFromLocation(location) {
  try {
    const search = new URLSearchParams(location?.search || '');
    const raw = search.get('next');
    if (raw) {
      try {
        return decodeURIComponent(raw);
      } catch (_) {
        return raw; // if it's not encoded, still try to validate as-is
      }
    }
    const fromPath = location?.state?.from?.pathname;
    if (fromPath) {
      const fromSearch = location?.state?.from?.search || '';
      return `${fromPath}${fromSearch}`;
    }
  } catch (_) {
    // Any parsing weirdness → treat as "no next".
  }
  return null;
}

/**
 * Validate a `next` candidate against the security rules above.
 * Returns the sanitized path (relative, single leading slash) if
 * allowed, otherwise null.
 *
 * @param {string|null} candidate — raw next path.
 * @param {string} role — the user's role.
 */
export function safeNextPath(candidate, role) {
  if (!candidate || typeof candidate !== 'string') return null;
  const trimmed = candidate.trim();
  if (!trimmed) return null;

  // Must be a relative same-origin path, starting with exactly one slash.
  if (!trimmed.startsWith('/')) return null;
  if (trimmed.startsWith('//')) return null; // protocol-relative

  for (const s of REJECT_SUBSTRINGS) {
    if (trimmed.includes(s)) return null;
  }

  // Never allow bouncing back to /login or /register.
  if (
    trimmed === '/login' ||
    trimmed.startsWith('/login?') ||
    trimmed === '/register' ||
    trimmed.startsWith('/register?')
  ) {
    return null;
  }

  // Public routes are allowed for any role.
  if (PUBLIC_PATH_PREFIXES.some((p) => trimmed === p || trimmed.startsWith(`${p}/`) || trimmed.startsWith(`${p}?`))) {
    return trimmed;
  }

  // Role-scoped routes.
  const allowed = ROLE_PATH_PREFIXES[role] || [];
  if (allowed.some((p) => trimmed === p || trimmed.startsWith(`${p}/`) || trimmed.startsWith(`${p}?`))) {
    return trimmed;
  }

  return null;
}

/**
 * One-shot helper for login/register handlers.
 * Returns the safe destination path (either the requested next or the
 * role's default dashboard).
 */
export function resolveDestination(location, role) {
  const candidate = extractNextFromLocation(location);
  const safe = safeNextPath(candidate, role);
  if (safe) return safe;
  return ROLE_DASHBOARDS[role] || '/';
}
